import { useState } from "react";
import { Download } from "lucide-react";
import { readJwt } from "../../auth/storage";
import { api, type CycleHistoryFilters } from "../../api/client";
import { Button } from "@/components/ui/button";

interface CsvExportProps {
  filters: CycleHistoryFilters;
  /** Disable while a request is in flight or no rows match. */
  disabled?: boolean;
}

/**
 * Downloads GET /api/cycles/history/csv for the current filters. The endpoint
 * is JWT-authenticated, so we fetch with the Bearer header, read a Blob, and
 * click a synthetic anchor. On error it surfaces an explicit message.
 */
export default function CsvExport({ filters, disabled }: CsvExportProps) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setPending(true);
    setError(null);
    try {
      const url = api.historyCsvUrl(filters);
      const jwt = readJwt();
      const res = await fetch(url, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) {
        setError(`Export failed: HTTP ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `dax-cycles-${stamp()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export échoué");
    } finally {
      setPending(false);
    }
  };

  return (
    <span className="csv-export flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={download}
        disabled={disabled || pending}
        data-testid="csv-export-button"
      >
        <Download className="size-3.5" aria-hidden />
        {pending ? "Export…" : "Export CSV"}
      </Button>
      {error && (
        <span className="text-loss text-xs" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}

function stamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
}
